import { withErrorBoundary } from 'app/components/error-boundary';
import { LogoRenderer } from 'app/design/branding/logo';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Input } from 'app/design/primitives/input';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';

import { useForm } from 'app/hooks/form';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useForgotPassword } from 'app/network/user';
import type { NavigationScreen, SpecificNavigationProp } from 'app/types/navigation';
import { type ForgotPasswordDTO } from 'app/types/user';
import { usePageTitle, useUISize } from 'app/utils/web-helpers';
import { useEffect } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, ScrollView, View } from 'react-native';
import * as yup from 'yup';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

const schema: yup.ObjectSchema<ForgotPasswordDTO> = yup.object({
  email: yup.string().email().required(),
});

const MINT_SCREEN_BG = theme.colors['surface.tertiary'];
const CARD_MAX_WIDTH = 440;

function ForgotPasswordFields({
  control,
  t,
}: {
  control: Control<ForgotPasswordDTO>;
  t: ReturnType<typeof useScopedTranslation>['t'];
}) {
  return (
    <>
      <Text fontWeight="700" marginBottom="spacing.general.x4" textAlign="center" variant="headerXSmall">
        {t('title')}
      </Text>

      <Text color="text.primary" marginBottom="spacing.general.x5" opacity={0.85} textAlign="center" variant="bodyBase">
        {t('subtitle')}
      </Text>

      <Controller
        {...{ control }}
        name="email"
        render={({ field, fieldState }) => (
          <Input
            autoCapitalize="none"
            autoComplete="email"
            autoCorrect={false}
            error={fieldState.error}
            fieldBorderRadius="radius.full"
            onBlur={field.onBlur}
            onChangeText={field.onChange}
            placeholder={t('emailFieldPlaceholder')}
            textContentType="emailAddress"
            value={field.value}
          />
        )}
      />
    </>
  );
}

function ResetPasswordButton({
  disabled,
  isLoading,
  onPress,
  placement,
  t,
}: {
  disabled: boolean;
  isLoading: boolean;
  onPress: () => void;
  placement: 'card' | 'accessory';
  t: ReturnType<typeof useScopedTranslation>['t'];
}) {
  return (
    <Button
      disabled={disabled}
      loading={isLoading}
      {...(placement === 'card'
        ? { marginTop: 'spacing.general.x12' }
        : { marginHorizontal: 'spacing.general.x4', marginTop: 'spacing.general.x4' })}
      onPress={onPress}
      variant="primary"
    >
      <Text color="surface.default" fontWeight="700" textAlign="center">
        {t('primaryButtonText')}
      </Text>
    </Button>
  );
}

export const ForgotPasswordScreenComponent = function ForgotPasswordScreenComponent({
  navigation,
}: {
  navigation: SpecificNavigationProp<'forgot-password'>;
}) {
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');
  const { isLargeUI } = useUISize();
  const { control, formState, handleSubmit } = useForm<ForgotPasswordDTO>({ schema });

  const setPageTitle = usePageTitle('forgot-password');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const {
    error,
    mutate,
    isPending: isLoading,
  } = useForgotPassword({
    onSuccess: (_, variables) => navigation.navigate('reset-password', { email: variables.email }, { pop: true }),
  });

  const onSubmit = handleSubmit((form) => mutate(form));

  if (isLargeUI) {
    return (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1, backgroundColor: MINT_SCREEN_BG }}
      >
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            paddingHorizontal: theme.spacing['spacing.general.x6'],
            paddingVertical: theme.spacing['spacing.general.x12'],
            width: '100%',
          }}
          keyboardShouldPersistTaps="handled"
        >
          <View
            style={{
              alignSelf: 'center',
              backgroundColor: theme.colors['surface.default'],
              borderRadius: theme.borderRadii['radius.xl'],
              maxWidth: CARD_MAX_WIDTH,
              paddingHorizontal: theme.spacing['spacing.general.x8'],
              paddingVertical: theme.spacing['spacing.general.x10'],
              width: '100%',
              ...(Platform.OS === 'web'
                ? {
                    shadowColor: '#1F2635',
                    shadowOffset: { width: 0, height: 8 },
                    shadowOpacity: 0.08,
                    shadowRadius: 32,
                  }
                : {
                    elevation: 4,
                    shadowColor: '#1F2635',
                    shadowOffset: { width: 0, height: 8 },
                    shadowOpacity: 0.08,
                    shadowRadius: 16,
                  }),
            }}
          >
            <ForgotPasswordFields control={control} t={t} />

            {error ? (
              <View style={{ marginTop: theme.spacing['spacing.general.x4'] }}>
                <RemoteError {...{ error, message: tAPI(error.message) }} />
              </View>
            ) : null}

            <ResetPasswordButton
              disabled={!formState.isValid}
              isLoading={isLoading}
              onPress={() => void onSubmit()}
              placement="card"
              t={t}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  return (
    <Page
      accessory={
        <Accessory>
          {error ? (
            <View style={{ marginHorizontal: theme.spacing['spacing.general.x4'] }}>
              <RemoteError {...{ error, message: tAPI(error.message) }} />
            </View>
          ) : null}

          <ResetPasswordButton
            disabled={!formState.isValid}
            isLoading={isLoading}
            onPress={() => void onSubmit()}
            placement="accessory"
            t={t}
          />
        </Accessory>
      }
      style={{ backgroundColor: theme.colors['surface.default'] }}
    >
      <Column marginHorizontal="spacing.general.x4" marginTop="spacing.general.x2">
        <ForgotPasswordFields control={control} t={t} />
      </Column>
    </Page>
  );
};

const ForgotPasswordScreenRouteComponent: NavigationScreen<'forgot-password'> = ({ navigation }) => {
  const { isLargeUI } = useUISize();
  const { t } = useScopedTranslation();
  const headerForegroundColor = theme.colors['text.primary'];

  useNavigationOptions(navigation, {
    headerBackVisible: false,
    headerLeft: () => (
      <Button
        onPress={navigation.goBack}
        accessibilityLabel={
          isLargeUI ? t('forgotPassword.close', { ns: 'accessibility' }) : t('goBack', { ns: 'accessibility' })
        }
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} color={headerForegroundColor} />
      </Button>
    ),
    headerStyle: {
      backgroundColor: isLargeUI ? MINT_SCREEN_BG : theme.colors['surface.tertiary-neutral'],
    },
    headerTintColor: headerForegroundColor,
    headerTitle: isLargeUI
      ? () => null
      : () => (
          <View style={{ justifyContent: 'center', paddingVertical: 4 }}>
            <LogoRenderer color={headerForegroundColor} width={132} />
          </View>
        ),
    headerTitleAlign: isLargeUI ? 'center' : 'left',
  });

  return <ForgotPasswordScreenComponent navigation={navigation} />;
};

const ForgotPasswordScreenWithErrorBoundary = withErrorBoundary(ForgotPasswordScreenRouteComponent);

export const ForgotPasswordScreen: NavigationScreen<'forgot-password'> = (props) => (
  <ForgotPasswordScreenWithErrorBoundary {...props} />
);
